import { faChevronLeft, faSave } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import React, { useMemo, useState } from 'react';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import VoxelInput from '../../../components/voxel/Forms/VoxelInput';
import VoxelSelect from '../../../components/voxel/Forms/VoxelSelect';
import { AnalysisColorPaletteGq } from '../../../graphql/projects/models/AnalysisColorPaletteGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { ColorPalette, parsePalette } from './ColorPalette';
import ColorPickerSection from './ColorPickerSection';
import ColorSwatchSection from './ColorSwatchSection';

export interface PalettePageProps {
  originalPalette: AnalysisColorPaletteGq;
  goBack: () => void;
  reload: () => Promise<unknown>;
}

export interface SelectedColorState {
  colorKey: keyof ColorPalette;
  colorIndex: number;
}

export interface FormState {
  id: number;
  title: string;
  palette: ColorPalette;
}

export const PalettePage: React.FC<PalettePageProps> = ({ originalPalette, goBack, reload }) => {
  const parsedPalette: ColorPalette = useMemo(
    () => parsePalette(originalPalette.themeJson),
    [originalPalette]
  );

  const updateMutation = projectsApiService.mutations.projects.useUpdateAnalysisColorPalette();
  const formBag = useFormik<FormState>({
    initialValues: {
      id: originalPalette.id,
      title: originalPalette.title,
      palette: parsedPalette,
    },
    enableReinitialize: true,
    onSubmit: (values) =>
      updateMutation({
        title: values.title,
        themeJson: JSON.stringify(values.palette),
        id: values.id,
      }).then(reload),
  });

  // Open color state
  const [selectedColorState, setSelectedColorState] = useState<SelectedColorState>({
    colorKey: 'performanceScale',
    colorIndex: 0,
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="absolute top-4 left-4">
        <VoxelIconButton
          icon={<FontAwesomeIcon icon={faChevronLeft} />}
          size="lg"
          color="white"
          onClick={goBack}
          disabled={formBag.isSubmitting}
        />
      </div>
      <div className="mt-8">
        <VoxelInput
          label="Palette title"
          labelPosition="top"
          value={formBag.values.title}
          onChange={(event) => formBag.setFieldValue('title', event.target.value)}
          disabled={formBag.isSubmitting}
        />
      </div>
      <div className="border-t border-gray-200" />
      <div className="grid grid-cols-2 gap-8">
        <VoxelSelect
          labelPosition="top"
          label="Label color"
          options={[
            { value: '#FFFFFF', title: 'White' },
            { value: '#000000', title: 'Black' },
          ]}
          selectedOptionValue={formBag.values.palette.textColor[0]}
          setSelectedOption={(option) =>
            formBag.setFieldValue('palette', {
              ...formBag.values.palette,
              textColor: [option.value],
            })
          }
        />
      </div>
      <div className="grid grid-cols-2 gap-8">
        <div>
          <ColorSwatchSection
            selectedColorState={selectedColorState}
            setSelectedColorState={setSelectedColorState}
            paletteState={formBag.values.palette}
            setPaletteState={(palette) => formBag.setFieldValue('palette', palette)}
          />
        </div>
        <ColorPickerSection
          selectedColorState={selectedColorState}
          paletteState={formBag.values.palette}
          setPaletteState={(palette) => formBag.setFieldValue('palette', palette)}
        />
      </div>
      <div className="border-t border-gray-200" />
      <div className="flex flex-row justify-end">
        <VoxelButton
          onClick={() => formBag.handleSubmit()}
          title="Save changes"
          startIcon={<FontAwesomeIcon icon={faSave} />}
          disabled={!formBag.dirty}
          color="gray"
          size="xl"
        />
      </div>
    </div>
  );
};

export default PalettePage;
